import { Grid, useTheme } from '@mui/material'
import { Approve, EventCoverButton, PlusIcon } from 'assets/icons'
import IconButton from 'components/buttons/IconButton'
import InputWithApproveButton from 'components/inputs/InputWithApproveButton'
import { useConnection } from 'components/providers/ConnectionProvider'
import { IEventDetails } from 'hooks/useEventDetails'
import { t } from 'i18next'
import { ChangeEvent, useEffect } from 'react'
import Logger from 'services/Logger'
import styled from 'styled-components'
import { DocType, TaskType } from 'types/api/changes'
import { useFilePicker } from 'use-file-picker'
import { is24meApp } from 'utils/appType'
import { compressBase64 } from 'utils/file'
import { capitalizeFirstChar } from 'utils/strings'

export interface NoteTitleInputProps {
  docType?: DocType
  isCurrentGroupIsGoogleCalendar: boolean
  onApproveNoteText: (s: string) => void
  onAttachFile?: (title: string, bytes: string) => void
  eventHook?: IEventDetails
  taskType?: TaskType
}

export default function NoteTitleInput(props: NoteTitleInputProps) {
  const theme = useTheme()
  const notePendingText = props.eventHook?.notePendingText ?? ''
  const compressedImage = props.eventHook?.notePendingBytes
  const onTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    props.eventHook?.setNotePendingText(event.target.value)
  }

  const {
    isCurrentGroupIsGoogleCalendar,
    onApproveNoteText,
    onAttachFile,
    taskType
  } = props
  const connection = useConnection()
  const { openFilePicker, filesContent } = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: false
  })

  function selectPic() {
    openFilePicker()
  }

  useEffect(() => {
    if (props.eventHook?.loading) {
      props.eventHook?.setNotePendingText('')
      props.eventHook?.setNotePendingBytes(undefined)
    }
  }, [props.eventHook?.loading])

  useEffect(() => {
    if (filesContent.length > 0) {
      if (connection.isConnectionAvailable()) {
        if (onAttachFile) {
          compressBase64(filesContent[0].content)
            .then((data) => {
              const defaultPhotoTitle = capitalizeFirstChar(t('photo'))

              props.eventHook?.setNotePendingBytes(data)
              props.eventHook?.setNotePendingText(defaultPhotoTitle)
            })
            .catch((e) =>
              Logger.red('Error while compressing image', e, 'NoteTitleInput')
            )
        }
      }
    }
  }, [filesContent])

  return (
    <Grid
      width={'100%'}
      display={'flex'}
      flexDirection={'row'}
      alignItems={'center'}
      mt={is24meApp() ? 2 : 0}
    >
      <StyledInput
        id="event-note-input"
        compressedImage={compressedImage}
        text={notePendingText}
        bringFocus={compressedImage ? true : false}
        onTextChange={onTextChange}
        doNotResevePlace
        leftIcon={
          <PlusIcon
            style={{ marginLeft: '2px' }}
            color={theme.palette.input.placeholder}
          />
        }
        hintText={`${t(
          isCurrentGroupIsGoogleCalendar ? 'addNote' : 'addNewNote'
        )}`}
        onApprove={(text: string) => {
          if (compressedImage) {
            onAttachFile?.(
              text ?? capitalizeFirstChar(t('photo')),
              compressedImage
            )
            props.eventHook?.setNotePendingText('')
            props.eventHook?.setNotePendingBytes(undefined)
          } else {
            onApproveNoteText(text)
          }
        }}
        approveIcon={
          <Approve
            id="event-note-input-approve"
            width={16}
            height={13}
            style={{
              color:
                theme.palette.mode === 'dark'
                  ? 'white'
                  : (theme.palette.input.placeholder as string),
              fill:
                theme.palette.mode === 'dark'
                  ? 'white'
                  : (theme.palette.input.placeholder as string)
            }}
          />
        }
      />
      {taskType !== TaskType.personalEvent && !compressedImage && (
        <IconButton onClick={selectPic} size="xs">
          <EventCoverButton
            style={{
              color: theme.palette.input.placeholder,
              width: 16,
              height: 13
            }}
          />
        </IconButton>
      )}
    </Grid>
  )
}

const StyledInput = styled(InputWithApproveButton)`
  color: ${(props) => (props.compressedImage ? 'blue' : 'black')};
  text-decoration: ${(props) => (props.compressedImage ? 'underline' : 'none')};

  & .Input__Input {
    color: ${(props) => (props.compressedImage ? 'blue' : 'black')};
  }
  width: 100%;
`
